/* eslint-disable react-hooks/exhaustive-deps */
import {
  EMenuOpenType,
  MenuWrapperProps,
  MenuWrapper as MenuWrapperTl,
  TIoTDevice,
  TIoTDeviceType,
  TIoTGroup,
} from '@treedis/tour-loader';
import { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react';
import { isMobileOnly } from 'react-device-detect';
import PublicAPI from '@/api/TreedisPublicDAO';
import { sendToGA } from '@/components/util';
import { TIoTMenu } from './types';
import { getBM, getIoT } from './utils';

/* EXPLANATION OF THE LOGIC IN THIS COMPONENT
 * This component is a wrapper for BM and IoT menus components from the tour-loader package.
 * Since the menu will overlap most of the popups in the tour (either from Matterport or from Treedis), we need to hide it if it's open and a popup is open and re-show it when the popup is closed. It's not possible to solve this problem with z-indexes. Plus, it's a better UX if the menu is hidden when a popup is open and then re-shown when the popup is closed.
 * To do this, we use MutationObservers to listen for changes in the DOM and check if the menu should be shown or hidden.
 * We have two different MutationObservers, one for Matterport popups and one for Treedis popups.
 * The Matterport MutationObserver is attached to the showcase iframe and listens for changes in the class attribute of the showcase.
 * The Treedis MutationObserver is attached to the .base-tour div and listens for changes in the childList and if the popup is added or removed.
 */

const MP_IFRAME_SELECTOR = '.showcase.showcase';
const TREEDIS_SELECTOR = '.base-tour';

// These are matterport triggers (ex: when a MP Image Tag popup is open)
// Classes
const showcaseTargets = [
  'modal-open',
  'side-panel tool-open',
  'overlay-layer attachment-overlay open',
  'panels panel-open panel-side panel-right',
];
// IDs
const baseTourTargets = [
  'tag-popup',
  'treedis-popup-container',
  'external-popup',
  'vsaction-popup',
  'showcase-modals',
  'contact-pop',
];

type Props = Omit<
  MenuWrapperProps,
  | 'settings'
  | 'colors'
  | 'contact'
  | 'address'
  | 'onIoTOpen'
  | 'onBMOpen'
  | 'onBMClose'
  | 'onIoTClose'
  | 'openType'
  | 'iotDevices'
  | 'iotGroups'
  | 'iotDevicesTypes'
> & {
  menuSettings: any;
  tour: any;
  tourEmail: any;
  sendTagToGA: (sid: string) => void;
  iotMenu?: TIoTMenu;
  iotDevices?: TIoTDevice[];
  toggleContactPopup: () => void;
  toggleExternalPopup: (url: string) => void;
  onOpen: () => void;
  onClose: () => void;
  handleLock: (d: TIoTDevice) => void;
};

function MenuWrapper(props: Props) {
  const {
    menuSettings,
    iotMenu,
    tour,
    tourEmail,
    iotDevices,
    toggleContactPopup,
    toggleExternalPopup,
  } = props;

  const [openType, setOpenType] = useState<EMenuOpenType>();

  // Needed to keep track of the openType when the menu is closed automatically by MutationObservers
  const openTypeRef = useRef<EMenuOpenType>();

  const [iotGroups, setIotGroups] = useState<TIoTGroup[]>();
  const [iotDevicesTypes, setIotDevicesTypes] = useState<TIoTDeviceType[]>();

  const [showcaseNode, setShowcaseNode] = useState<HTMLIFrameElement | null>(null);
  const [showcaseTrigger, setShowcaseTrigger] = useState<string>();
  const [baseTourTrigger, setBaseTourTrigger] = useState<string>();

  const [iotSettings, iotColors] = useMemo(() => getIoT(iotMenu), [iotMenu]);
  const [bmSettings, bmColors] = useMemo(() => getBM(menuSettings), [menuSettings]);

  const isBM = !openType ? true : openType === EMenuOpenType.BM;

  useEffect(() => {
    if (bmSettings.openOnLaunch || bmSettings.alwaysOpen) {
      setOpenType(EMenuOpenType.BM);
      props.onOpen();
    } else if (iotMenu?.active && (iotSettings?.openOnLaunch || iotSettings?.alwaysOpen)) {
      setOpenType(EMenuOpenType.IoT);
      props.onOpen();
    }
  }, [
    bmSettings.alwaysOpen,
    bmSettings.openOnLaunch,
    iotMenu?.active,
    iotSettings?.alwaysOpen,
    iotSettings?.openOnLaunch,
  ]);

  const fetchData = useCallback(
    async (apiMethod, cb: (res: any) => void) => {
      const res = await apiMethod({
        query: {
          tourId: tour.id,
        },
      });

      if (res) {
        cb(res);
      }
    },
    [tour?.id],
  );

  useEffect(() => {
    (async () => {
      if (!tour?.id || !iotMenu.active) return;

      await Promise.all([
        fetchData(PublicAPI.getIoTGroups, (res) => setIotGroups(res.groups)),
        fetchData(PublicAPI.getIoTDevicesTypes, (res) => setIotDevicesTypes(res.data)),
      ]);
    })();
  }, [fetchData, iotMenu?.active, tour?.id]);

  const shouldObserveMutations = useMemo(
    () => props.open || showcaseTrigger || baseTourTrigger,
    [props.open, showcaseTrigger, baseTourTrigger],
  );

  // Get Showcase Iframe Node
  // Since it may take some time for the showcase to load, we need to keep trying to get the showcase iframe until we get it
  useEffect(() => {
    const getShowcaseIframe = (retryCount = 0) => {
      const node = document.querySelector(MP_IFRAME_SELECTOR);

      if (node) {
        setShowcaseNode(node as HTMLIFrameElement);
      } else if (retryCount < 10) {
        setTimeout(() => getShowcaseIframe(retryCount + 1), 1000);
      }
    };

    getShowcaseIframe();
  }, []);

  // MP SHOWCASE POPUPS LOGIC TO HIDE BM
  useEffect(() => {
    // Create a new instance of MutationObserver
    const observer = new MutationObserver((mutationsList) => {
      // If we have a currentShowcaseTrigger, then we should check if the menu should be shown
      if (showcaseTrigger) {
        const shouldShowMenu = mutationsList.some(
          (mutation) => showcaseTrigger === mutation.oldValue,
        );

        if (shouldShowMenu) {
          setOpenType(openTypeRef.current || EMenuOpenType.BM);
          props.onOpen();
          setShowcaseTrigger(undefined);
        }
      } else {
        // If we don't have a currentShowcaseTrigger, then we should check if the menu should be hidden
        const shouldHideMenu = mutationsList.some((mutation) => {
          // @ts-ignore
          const target: string = mutation.target.className;
          if (showcaseTargets.includes(target)) {
            setShowcaseTrigger(target);
            return true;
          }
          return false;
        });

        if (shouldHideMenu) {
          props.onClose();
          openTypeRef.current = openType;
        }
      }
    });

    // Start observing the target node and its descendants for DOM changes
    if (showcaseNode && showcaseNode.contentDocument && shouldObserveMutations) {
      observer.observe(showcaseNode.contentDocument, {
        attributes: true,
        attributeOldValue: true,
        subtree: true,
        attributeFilter: ['class'],
      });
    } else {
      observer.disconnect();
    }

    return () => {
      // Stop observing when the component unmounts
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showcaseNode, shouldObserveMutations, showcaseTrigger]);

  // TREEDIS POPUPS LOGIC TO HIDE BM
  useEffect(() => {
    const node = document.querySelector(TREEDIS_SELECTOR);
    const observer = new MutationObserver((mutationsList) => {
      // If we have a currentBaseTourTrigger, then we should check if the menu should be shown
      if (baseTourTrigger) {
        // Checking for addedNodes
        // @ts-ignore
        const shouldShowMenu = mutationsList.some(
          (mutation) => baseTourTrigger === mutation.removedNodes?.[0]?.id,
        );

        if (shouldShowMenu) {
          setOpenType(openTypeRef.current || EMenuOpenType.BM);
          props.onOpen();
          setBaseTourTrigger(undefined);
        }
      } else {
        // If we don't have a currentBaseTourTrigger, then we should check if the menu should be hidden
        const shouldHideMenu = mutationsList.some((mutation) => {
          // @ts-ignore
          const target: string = mutation.addedNodes?.[0]?.id;

          // Checking for removedNodes
          if (baseTourTargets.includes(target)) {
            setBaseTourTrigger(target);
            return true;
          }
          return false;
        });

        if (shouldHideMenu) {
          props.onClose();
          openTypeRef.current = openType;
        }
      }
    });

    if (node && shouldObserveMutations) {
      observer.observe(node, { childList: true, subtree: true });
    } else {
      observer.disconnect();
    }

    return () => {
      observer.disconnect();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseTourTrigger, props.open, shouldObserveMutations]);

  const handleAddressClick = useCallback(() => {
    toggleExternalPopup(
      tour?.id === 5238 // Temporary fix for Skoda
        ? 'https://www.skoda.co.il/show-rooms'
        : `https://www.google.com/maps/embed/v1/place?key=AIzaSyAxJYg1iR0WMy4vpFa3zB7E0Dx7lz8h5dk&q=${tour?.address}`,
    );

    sendToGA('event', {
      eventCategory: 'Contact',
      eventAction: 'click',
      eventLabel: 'map',
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour?.id, tour?.address]);

  const handleOpen = useCallback(
    (type: EMenuOpenType) => {
      setOpenType(type);
      props.onOpen();
    },
    [props],
  );

  const handleClose = useCallback(() => {
    // We need a short delay to avoid a bug where menu height is reduced before the menu is closed
    setTimeout(() => {
      setOpenType(undefined);
    }, 500);
    props.onClose();
  }, [props]);

  const alwaysOpen = !isMobileOnly && (iotSettings?.alwaysOpen || bmSettings.alwaysOpen);

  return (
    <MenuWrapperTl
      {...props}
      openType={props.open ? openType || EMenuOpenType.BM : undefined}
      iotDevices={iotDevices || []}
      iotGroups={iotGroups || []}
      iotDevicesTypes={iotDevicesTypes || []}
      onBMOpen={tour?.menuEnabled ? () => handleOpen(EMenuOpenType.BM) : undefined}
      onIoTOpen={iotMenu?.active ? () => handleOpen(EMenuOpenType.IoT) : undefined}
      onBMClose={alwaysOpen ? undefined : handleClose}
      onIoTClose={alwaysOpen ? undefined : handleClose}
      showBothTriggers={tour?.menuEnabled && iotMenu?.active}
      settings={{
        ...(isBM ? bmSettings : iotSettings),
        openOnHoverBm: menuSettings?.openOnHover,
        openOnHoverIot: iotMenu?.openOnHover,
        tooltipOnBm: menuSettings?.menuTooltipOn,
        tooltipOnIot: !iotMenu?.disableMenuTooltip,
        fullScreenMobile: bmSettings.fullScreenMobile || iotSettings?.fullScreenMobile,
      }}
      colors={{
        ...(isBM ? bmColors : iotColors),
        text: (isBM ? bmColors.text : iotColors.text) || '#ffffff',
        bmBg: bmColors.bg || '#000000',
        iotBg: iotColors.bg || '#000000',
        bmText: bmColors.text || '#ffffff',
        iotText: iotColors.text || '#ffffff',
      }}
      book={{
        url: menuSettings?.bookUrl,
        label: menuSettings?.bookText,
      }}
      contact={
        tour?.contactForm
          ? {
              phone: tour?.client?.phone || tour?.client?.email || tourEmail?.contactPhone,
              onClick: toggleContactPopup,
            }
          : undefined
      }
      address={{
        label: tour?.address,
        onClick: handleAddressClick,
      }}
    />
  );
}

export default memo(MenuWrapper);
